import { helpers } from '@vuelidate/validators';
import { RESERVED_CUTOM_ATTRIBUTE_KEYS } from './customAttributes';
import dayjs from '~/utils/dayjs';
import { isValidPhoneNumber } from './phoneNumber';
import { nextTick, unref, type Ref } from 'vue';
import type { Validation, ErrorObject } from '@vuelidate/core';

export const csvInjection = helpers.withMessage('Cannot start with @, =, - or +', (value?: string) => {
    if (!value) {
        return true;
    }

    return !(value.startsWith('@') || value.startsWith('=') || value.startsWith('-') || value.startsWith('+'));
});

export const teamCustomAttributeKeyFirstLetter = helpers.withMessage(
    'Cannot start with non-alphanumeric',
    (value?: string) => {
        if (!value) {
            return true;
        }

        return /[a-zA-Z0-9]/.test(value.charAt(0));
    }
);

export const groupCustomAttributeKey = helpers.withMessage(
    'You can use only alphanumeric letters and the following symbols: _-',
    helpers.regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/)
);

export const startAlphanumericDashUnderscore = helpers.withMessage(
    'Can’t start with `_` or `-`. No special characters: only `a-z`, `0-9`, and `_-`',
    helpers.regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/)
);

export const alphanumericDashUnderscore = helpers.withMessage(
    'You can use only alphanumeric letters and the following symbols: _-',
    helpers.regex(/^[a-zA-Z0-9_-]*$/)
);

export const customAttributeKey = helpers.withMessage(
    "You can use only alphanumeric letters, space and the following symbols: _-!#$%&'()*+,./:?@| and may not start or end with a space",
    helpers.regex(
        /^(?:[a-zA-Z0-9_\-!#$%&'()*+,./:?@|](?:[a-zA-Z0-9_\- !#$%&'()*+,./:?@|]*[a-zA-Z0-9_\-!#$%&'()*+,./:?@|])?)?$/
    )
);
export const customAttributeValue = helpers.withMessage(
    "You can use only alphanumeric letters, space and the following symbols: :.@#$%&'*(),!?\\-=_+[]/",
    helpers.regex(/^[a-zA-Z0-9:.@#$%&'*(),!?\-\\=_+[\] /]*$/)
);

export const alphanumericDashUnderscoreSpace = helpers.withMessage(
    'You can use only alphanumeric letters, space and the following symbols: _-',
    helpers.regex(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/)
);

export const groupAndOrgUnitName = helpers.withMessage(
    "You can use only alphanumeric letters, space and the following symbols: _-.,;:'",
    helpers.regex(/^[a-zA-Z0-9][a-zA-Z0-9 _\-.,;:']*$/)
);

export const phoneNumber = helpers.withMessage('Should be a valid phone number', (value?: string) => {
    if (!value) {
        return true;
    }

    return isValidPhoneNumber(value);
});

// These three validators mirror the backend SftpUrlRule's prefix,
// credential, and suffix checks — keep in sync.
export const startsWithSftp = helpers.withMessage('The URL must start with sftp://', (value?: string) => {
    if (!value) {
        return true;
    }

    return value.startsWith('sftp://');
});

export const noSftpEmbeddedCredentials = helpers.withMessage(
    'The URL cannot contain embedded credentials',
    (value?: string) => {
        if (!value) {
            return true;
        }

        return !value.includes('@');
    }
);

export const validSftpUrlFormat = helpers.withMessage(
    'The URL must end with / for directories or with .csv or .zip for files',
    (value?: string) => {
        if (!value) {
            return true;
        }

        // The suffix check ignores any query string or fragment, matching
        // the backend, which checks only the path component.
        const path = value.replace(/[?#].*$/, '').match(/^sftp:\/\/[^/]*(\/.*)?$/)?.[1] || '/';

        if (path.endsWith('/')) {
            return true;
        }

        const lowerPath = path.toLowerCase();
        return lowerPath.endsWith('.csv') || lowerPath.endsWith('.zip');
    }
);

export const reservedCustomAttributesKeys = helpers.withMessage(
    'Reserved custom attribute keys are not allowed',
    (value: string) => !RESERVED_CUTOM_ATTRIBUTE_KEYS.includes(value)
);

interface IsAfterDateOptions {
    date?: (() => Date) | { value: Date };
    required?: boolean;
    message?: Ref<string> | string | (() => string);
}

export const isAfterDate = ({ date = () => new Date(), required = true, message }: IsAfterDateOptions = {}) => {
    const resolvedMessage =
        typeof message === 'function' ? message() : typeof message === 'string' ? message : message?.value;

    return helpers.withMessage(resolvedMessage || 'Date must be after now', (value?: string) => {
        if (!required && !value) {
            return true;
        }
        return dayjs(value).isSameOrAfter(typeof date === 'function' ? date() : date.value, 'minutes');
    });
};

export function displayFirstError(errors?: string[] | ErrorObject[] | { $errors: ErrorObject[] }) {
    if (!errors) {
        return undefined;
    }

    if (!Array.isArray(errors)) {
        errors = errors.$errors;
    }

    if (typeof errors[0] === 'string') {
        return errors[0];
    }

    const message = errors?.[0]?.$message;

    return message ? unref(message) : undefined;
}

export function focusInvalidField(): boolean {
    const modalContents = document.querySelectorAll<HTMLElement>('.vx-modal-content');

    // only cares about last opened modal
    let lastModalContent = modalContents[modalContents.length - 1];

    const parent = lastModalContent || document;

    let firstInvalidField = parent.querySelector<HTMLElement>('[aria-invalid="true"]');

    // if cant find on modal try on document again
    if (!firstInvalidField && lastModalContent) {
        firstInvalidField = document.querySelector<HTMLElement>('[aria-invalid="true"]');
        lastModalContent = undefined;
    }

    if (!firstInvalidField) {
        return false;
    }

    const fieldRect = firstInvalidField.getBoundingClientRect();

    if (lastModalContent) {
        const modalRect = lastModalContent.getBoundingClientRect();
        const relativeTop = fieldRect.top - modalRect.top + lastModalContent.scrollTop;

        lastModalContent.scrollTo({
            top: Math.max(0, relativeTop - 80),
        });
    } else {
        window.scrollTo({
            top: window.scrollY + fieldRect.top - 160,
            behavior: 'smooth',
        });
    }

    firstInvalidField.focus?.();
    return true;
}

/**
 * Validate the rules on a vuelidate instance while focusing on the first error
 */
export async function validateRules(vuelidateInstance: Ref<Validation>): Promise<boolean> {
    try {
        vuelidateInstance.value.$clearExternalResults();
        // eslint-disable-next-line no-empty
    } catch {}

    const valid = await vuelidateInstance.value.$validate();

    await nextTick();
    focusInvalidField();

    return valid;
}

export function jsonApiSpecErrorsToLaravel(
    errors: { detail: string; source?: { pointer: string } }[]
): Record<string, string[]> {
    const laravelFormat: Record<string, string[]> = {};
    errors.forEach((error) => {
        if (!error.detail || !error.source?.pointer) {
            return;
        }

        const field = error.source.pointer.split('/').pop();
        if (field) {
            laravelFormat[field] = laravelFormat[field] || [];
            laravelFormat[field].push(error.detail);
        }
    });

    return laravelFormat;
}
